import { useCallback, useState } from 'react'
import type { ClaudeSession } from '@/features/events/conversation/state/types'
import { useCrypto } from '@/infra/crypto/context'
import { buildSessionResumePayload, commandClient, encryptCommand } from '../api'

interface UseResumeSessionReturn {
  readonly resumeSession: (
    session: ClaudeSession,
    initialMessage: string | null,
  ) => Promise<boolean>
  readonly isSubmitting: boolean
}

export function useResumeSession(): UseResumeSessionReturn {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { encrypt, activeKeyVersion, isReady } = useCrypto()

  const resumeSession = useCallback(
    async (session: ClaudeSession, initialMessage: string | null): Promise<boolean> => {
      if (!isReady || activeKeyVersion === null) {
        throw new Error('Encryption is not available. Cannot resume session.')
      }

      setIsSubmitting(true)

      try {
        const payload = buildSessionResumePayload(session, initialMessage)
        const encryptedPayload = await encryptCommand(payload, activeKeyVersion, encrypt)
        const result = await commandClient.submit(encryptedPayload)

        if (!result.success) {
          throw new Error(result.message)
        }

        return true
      } finally {
        setIsSubmitting(false)
      }
    },
    [encrypt, activeKeyVersion, isReady],
  )

  return { resumeSession, isSubmitting }
}
